// store/modules/action-helpers.js

/**
  * Creates a set of standard CRUD mutations for a Vuex module.
  * @returns {object} - An object containing SET_ITEMS, SET_PAGINATION, and SET_CURRENT_ITEM mutations.
  */
export const crudMutations = () => {
  const mutations = {
    SET_ITEMS (state, items) {
      state.items = items
    },
    SET_PAGINATION (state, { count, currentPage }) {
      state.count = count
      state.currentPage = currentPage
    },
    SET_CURRENT_ITEM (state, item) {
      state.currentItem = item
    },
    SET_LIST_QUERY (state, { filters, page }) {
      state.listQuery = {
        filters: { ...filters },
        page
      }
    }
  }

  return mutations
}
/**
 * Creates a set of standard read-only actions for a Vuex module.
 * @param {object} config - The configuration object.
 * @param {string} config.basePath - The base API path without leading/trailing slashes (e.g., 'api/shipment').
 * @returns {object} - An object containing list, read, save, patch, and delete actions.
 */
export const readOnlyActions = ({ basePath }) => {
  const actions = {
    /**
     * Fetches a list of items from the API.
     */
    async list ({ commit, dispatch }, params) {
      return dispatch('list', { basePath, params }, { root: true })
        .then(response => {
          commit('SET_ITEMS', response.results)
          let page = 1
          if (params) {
            page = params.page || 1
          }
          commit('SET_PAGINATION', { count: response.count, currentPage: page })

          return response.results
        })
        .catch(error => {
          throw error
        })
    },

    /**
     * Fetches a single item by its ID.
     */
    async read ({ commit, dispatch }, id) {
      return dispatch('read', { basePath, id }, { root: true })
        .then(response => {
          commit('SET_CURRENT_ITEM', response)
          return response
        })
        .catch(error => {
          throw error
        })
    }
  }

  return actions
}

/**
 * Creates a set of standard CRUD actions for a Vuex module.
 * @param {object} config - The configuration object.
 * @param {string} config.basePath - The base API path without leading/trailing slashes (e.g., 'api/shipment').
 * @returns {object} - An object containing list, read, save, patch, and delete actions.
 */
export const crudActions = ({ basePath }) => {
  const actions = {
    ...readOnlyActions({ basePath }),
    /**
     * Saves an item (creates a new one or updates an existing one).
     */
    async save ({ commit, dispatch }, data) {
      return dispatch('save', { basePath, data, id: data.id }, { root: true })
        .then(response => {
          commit('SET_CURRENT_ITEM', response)
          return response
        })
        .catch(error => {
          throw error
        })
    },

    /**
     * Partially updates an item by its ID.
     */
    async patch ({ commit, dispatch }, data) {
      return dispatch('patch', { basePath, data, id: data.id }, { root: true })
        .then(response => {
          commit('SET_CURRENT_ITEM', response)
          return response
        })
        .catch(error => {
          throw error
        })
    },

    /**
     * Deletes an item by its ID.
     */
    async delete ({ commit, dispatch }, id) {
      return dispatch('delete', { basePath, id }, { root: true })
        .then(response => {
          commit('SET_CURRENT_ITEM', null)
          return response
        })
        .catch(error => {
          throw error
        })
    }
  }

  return actions
}

/**
  * Creates attachment-related actions for a Vuex module.
  * @param {object} config - The configuration object.
  * @param {string} config.basePath - The base API path without leading/trailing slashes (e.g., 'api/shipment').
  * @returns {object} - An object containing listAttachments, saveAttachments, and zipAttachments actions.
  */
export const attachmentActions = ({ basePath }) => {
  const actions = {
    // All attachment-related actions from the original store
    async listAttachments ({ dispatch }, { id, params }) {
      return dispatch('list', { basePath: `${basePath}/${id}/attachments`, params }, { root: true })
        .then(response => { return response.data })
        .catch(error => { throw error })
    },
    async saveAttachments ({ dispatch }, { id, formData }) {
      return dispatch('save', { basePath: `${basePath}/${id}/attachments`, data: formData }, { root: true })
        .then(response => { return response.data })
        .catch(error => { throw error })
    },
    async zipAttachments ({ dispatch }, { id, filters }) {
      return dispatch('downloadFiles', { url: `${basePath}/${id}/download_zip`, params: filters }, { root: true })
        .then(response => { return response })
        .catch(error => { throw error })
    }
  }

  return actions
}

/**
  * Creates shipment-specific actions for a Vuex module.
  * @param {object} config - The configuration object.
  * @param {string} config.basePath - The base API path without leading/trailing slashes (e.g., 'api/shipment').
  * @returns {object} - An object containing shipment-specific actions.
  */
export const shipmentActions = ({ basePath }) => {
  const actions = {
    // Fetch QR code for a specific shipment
    async getQR ({ dispatch }, id) {
      return dispatch('request', { method: 'get', url: `${basePath}/${id}/qr` }, { root: true })
        .then(response => { return response.data })
        .catch(error => { throw error })
    },

    // Fetch available dates for receptions
    async availableDates ({ dispatch }, params) {
      return dispatch('list', { basePath: `${basePath}/available_dates`, params: params }, { root: true })
        .then(response => {
          return response.results
        })
        .catch(error => { throw error })
    },

    // Send SMS related to a specific shipment
    async sendSms ({ dispatch }, data) {
      return dispatch('save', { basePath: `${basePath}/${data.id}/send_sms`, data: { msg: data.message } }, { root: true })
        .then(response => {
          return response
        })
        .catch(error => { throw error })
    }
  }

  return actions
}



// WEBPACK FOOTER //
// ./src/store/modules/action-helpers.js